// Store row shapes. Field names mirror the Apple `*Record` structs and the Android store models
// (docs/ARCHITECTURE.md "Shared models"): a human diffing ios/Sources/Store against this file
// should see the same names. Epoch-millisecond times are plain numbers (safe up to 2^53).

export enum PostState {
  Synced = 0,
  Pending = 1,
  Failed = 2,
}

export interface TeamRecord {
  id: string;
  displayName: string;
  name: string;
  description: string | null;
  updateAt: number;
  /** Mattermost's `last_team_icon_update` — the icon cache key; 0 = no icon, never fetch. */
  lastTeamIconUpdate: number;
}

export interface ChannelRecord {
  id: string;
  /** EMPTY for "D"/"G" — they belong to no team. */
  teamId: string;
  /** "O" | "P" | "D" | "G" */
  type: string;
  /** EMPTY for "D"; unreliable for "G" — derive names (docs/CHANNEL-LIST.md). */
  displayName: string;
  /** For "D": "<userIdA>__<userIdB>". */
  name: string;
  header: string | null;
  purpose: string | null;
  totalMsgCount: number;
  lastPostAt: number;
  updateAt: number;
  deleteAt: number;
  totalMsgCountRoot: number;
}

export interface ChannelMemberRecord {
  channelId: string;
  userId: string;
  msgCount: number;
  mentionCount: number;
  lastViewedAt: number;
  /** notify_props.mark_unread: "all" | "mention" ("mention" = muted). */
  markUnread: string | null;
  msgCountRoot: number;
  mentionCountRoot: number;
  /** notify_props.push; null = never synced, treat as "default". */
  notifyPush: string | null;
  /** The server's `last_update_at` for this row (shared/API.md §8.3). */
  serverUpdateAt: number;
}

export interface UserRecord {
  id: string;
  username: string;
  nickname: string | null;
  firstName: string | null;
  lastName: string | null;
  updateAt: number;
  email: string;
  lastPictureUpdate: number;
  /** 0 = active (shared/API.md §2.4). */
  deleteAt: number;
  /** RAW `props.customStatus` JSON or "" (shared/API.md §33.5). */
  customStatusJSON: string;
}

export interface PostRecord {
  id: string;
  channelId: string;
  userId: string;
  createAt: number;
  updateAt: number;
  deleteAt: number;
  message: string;
  rootId: string | null;
  pendingId: string | null;
  state: PostState;
  /** `system_*` for server-written notices, "" for messages. */
  type: string;
  /** Raw props JSON, or null. */
  props: string | null;
  isPinned: boolean;
  /** Non-zero exactly when the author edited the MESSAGE. */
  editAt: number;
  /** The one preview card this post earned (shared/API.md §12.4), serialized. */
  embedJson: string | null;
  /** "important" | "urgent" | "" */
  priority: string;
}

export interface CategoryRecord {
  id: string;
  teamId: string;
  /** "favorites" | "channels" | "direct_messages" | "custom" */
  type: string;
  displayName: string;
  /** "alpha" | "recent" | "manual" */
  sorting: string;
  sortOrder: number;
  collapsed: boolean;
  muted: boolean;
}

export interface PostFileRecord {
  fileId: string;
  postId: string;
  channelId: string;
  name: string;
  size: number;
  mimeType: string;
  width: number;
  height: number;
  miniPreview: string | null;
  sortOrder: number;
}

export interface ReactionRecord {
  postId: string;
  userId: string;
  emojiName: string;
  createAt: number;
}

export interface DraftRecord {
  channelId: string;
  rootId: string;
  message: string;
  updatedAt: number;
}

export interface ThreadRecord {
  id: string;
  teamId: string;
  channelId: string;
  replyCount: number;
  lastReplyAt: number;
  lastViewedAt: number;
  unreadReplies: number;
  unreadMentions: number;
  isUrgent: boolean;
  isFollowing: boolean;
  rootUserId: string;
  rootMessage: string;
  rootCreateAt: number;
}

export interface PostSeenSummaryRecord {
  postId: string;
  channelId: string;
  audienceCount: number;
  seenCount: number;
  anySeen: boolean;
  allSeen: boolean;
  updatedAt: number;
}
